import type { HelpCategory, HelpQuestion } from './types';

function stripMarkdown(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_#>-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface QuestionMatch {
  inQuestion: boolean;
  inAnswer: boolean;
}

export function matchQuestion(question: HelpQuestion, query: string): QuestionMatch {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return { inQuestion: false, inAnswer: false };
  }
  return {
    inQuestion: question.question.toLowerCase().includes(needle),
    inAnswer: stripMarkdown(question.answer).toLowerCase().includes(needle),
  };
}

/** Drops non-matching questions and categories entirely, rather than just flagging them. */
export function filterHelpCategories(categories: HelpCategory[], query: string): HelpCategory[] {
  if (!query.trim()) {
    return categories;
  }
  return categories
    .map((category) => ({
      ...category,
      questions: category.questions.filter((question) => {
        const { inQuestion, inAnswer } = matchQuestion(question, query);
        return inQuestion || inAnswer;
      }),
    }))
    .filter((category) => category.questions.length > 0);
}

/** Plain-text excerpt around the match, for when a query matches inside the (markdown) answer body. */
export function getAnswerSnippet(
  question: HelpQuestion,
  query: string,
  contextLength = 60,
): string | undefined {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return undefined;
  }
  const plain = stripMarkdown(question.answer);
  const index = plain.toLowerCase().indexOf(needle);
  if (index === -1) {
    return undefined;
  }
  const start = Math.max(0, index - contextLength);
  const end = Math.min(plain.length, index + needle.length + contextLength);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < plain.length ? '…' : '';
  return `${prefix}${plain.slice(start, end)}${suffix}`;
}
